import React, { useEffect, useState, useMemo } from 'react'
import styles from './styles.module.css'
import Button from '../../../components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { Checkbox, Input, Select } from '../../../components/ModalInputs'
import * as Yup from 'yup'
import {
  createPeriod,
  updatePeriod,
  getPeriodExclusions,
  createPeriodExclusion,
  updatePeriodExclusion,
  deletePeriodExclusion,
} from '../../../actions/periods'
import moment from 'moment'
import { getSchoolYears, getShifts } from '../../../actions/common'
import cn from 'classnames'
import Confirmation from '../../../components/Confirmation/Confirmation'
import ModalFooter from '../../../components/ModalFooter/ModalFooter'
import Exceptions from './Exceptions/Exceptions'
import { minutesToTime } from '../../../utils/classBook'
import { createNotification } from '../../../components/Notifications/Notifications'


const validationSchema = Yup.object({
  name: Yup.string().required('обязательное поле').max(256, 'Должно быть не более 256 символов'),
  s_year_id: Yup.string().required('обязательное поле'),
  dopstatus: Yup.string().required('обязательное поле'),
  short_name: Yup.string().required('обязательное поле').max(11, 'Должно быть не более 11 символов'),

  starttime: Yup.string().required('обязательное поле'),
  stoptime: Yup.string()
    .required('обязательное поле')
    .test('not-equal-start', 'Время начала не может совпадать с временем окончания', function (value) {
      return !value || value !== this.parent.starttime
    }),
});

const makeTempId = () => `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`

const normalizeServerExclusion = (raw) => {
  const type = raw.date ? 'date' : (raw.day_of_week !== null && raw.day_of_week !== undefined ? 'dow' : 'class')
  return {
    peid: raw.peid,
    _status: 'clean',
    starttime: raw.from_time,
    stoptime: raw.to_time,
    type,
    date: raw.date,
    day_of_week: raw.day_of_week,
    gid: raw.gid,
  }
}

// from_time/to_time у исключений - строки "HH:MM", в отличие от starttime/stoptime периода (минуты)
const buildExclusionPayload = (item) => {
  const payload = {
    from_time: item.starttime,
    to_time: item.stoptime,
  }
  if (item.type === 'date') payload.date = item.date
  if (item.type === 'dow') payload.day_of_week = item.day_of_week
  if (item.type === 'class') payload.gid = item.gid
  return payload
}

export default function Main({ closeModal, data, search, addMode, list }) {

  const dispatch = useDispatch()
  const { loading } = useSelector(state => state.terms);
  const { schoolYears, shifts } = useSelector(state => state.common);
  const { exclusions: serverExclusions } = useSelector(state => state.periods);
  const [confirmation, setConfirmation] = useState(false);
  const [open, setOpen] = useState(false);
  const [pendingExclusions, setPendingExclusions] = useState([]);
  const [deletedExclusionIds, setDeletedExclusionIds] = useState([]);

  // Сбрасываем состояние вкладки при закрытии модального окна или изменении данных
  useEffect(() => {
    if (!data) {
      setOpen(false);
    }
  }, [data]);

  // Буфер исключений: для существующего периода — подгружаем с сервера, для нового — начинаем с пустого списка
  useEffect(() => {
    if (data?.lid) {
      dispatch(getPeriodExclusions(data.lid))
    } else {
      setPendingExclusions([])
      setDeletedExclusionIds([])
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (data?.lid) {
      setPendingExclusions((serverExclusions || []).map(normalizeServerExclusion))
      setDeletedExclusionIds([])
    }
  }, [serverExclusions, data?.lid]);

  const acceptConfirmation = () => {
    closeModal();
    setConfirmation(false);
  };

  const rejectConfirmation = () => {
    setConfirmation(false);
  };

  useEffect(() => {
    if (!schoolYears) dispatch(getSchoolYears())
    if (!shifts) dispatch(getShifts())
  }, [dispatch, schoolYears, shifts])

  // Наличие колонки is_additional в БД определяем по присутствию поля в ответе API
  const hasIsAdditional = useMemo(() => {
    if (data?.lid) return 'is_additional' in data
    if (list?.length) return 'is_additional' in list[0]
    return true
  }, [data, list])

  const getCorrectPayload = (values) => {
    let { starttime, stoptime, is_additional, ...rest } = values
    starttime = moment.duration(starttime).asMinutes()
    stoptime = moment.duration(stoptime).asMinutes()
    const payload = { ...rest, starttime, stoptime }
    if (hasIsAdditional) {
      payload.is_additional = rest.status ? (is_additional ? 1 : 0) : null
    }
    return payload
  }

  const applyExclusions = async (lid) => {
    for (const item of pendingExclusions) {
      const payload = buildExclusionPayload(item)
      if (item._status === 'new') {
        await dispatch(createPeriodExclusion(lid, payload))
      } else if (item._status === 'dirty') {
        await dispatch(updatePeriodExclusion(lid, item.peid, payload))
      }
    }
    for (const peid of deletedExclusionIds) {
      await dispatch(deletePeriodExclusion(lid, peid))
    }
  }

  // Новый период создаётся вместе со всеми исключениями одной транзакцией (см. POST /period)
  const handleCreatePeriod = async (values) => {
    const payload = getCorrectPayload(values)
    payload.exclusions = pendingExclusions.map(buildExclusionPayload)
    const result = await dispatch(createPeriod(payload, search))
    if (!result?.ok) {
      createNotification('error', result?.message || 'Не удалось создать период')
      return
    }
    closeModal()
  }

  const handleUpdatePeriod = async (values) => {
    const { lid, ...rest } = values
    const payload = getCorrectPayload(rest)
    const result = await dispatch(updatePeriod(lid, payload, search))
    if (!result?.ok) {
      createNotification('error', result?.message || 'Не удалось сохранить период')
      return
    }
    await applyExclusions(lid)
    closeModal()
  }


  const {
    values,
    errors,
    touched,
    submitCount,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue
  } = useFormik({
    initialValues: addMode
      ? { s_year_id: search.year, starttime: '', stoptime: '', status: 0, dopstatus: 0, outstatus: 0, short_name: '', }
      : {
        ...data,
        starttime: minutesToTime(data.starttime),
        stoptime: minutesToTime(data.stoptime),
        s_year_id: search.year,
        lid: data.lid
      },
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: addMode ? handleCreatePeriod : handleUpdatePeriod
  })

  const error = (fieldName) => (touched[fieldName] || submitCount) && errors[fieldName]

  const renderOptions = ({ name, xp_key, sid }) => <option key={sid ?? xp_key} value={sid || xp_key}>{name}</option>

  // Запрет снимать "Учебный период"/"Доп. образование" у периода, занятого в расписании (ТЗ 2.4),
  // проверяется на сервере (PUT /period/:lid -> 400) - в ответе период такого флага не содержит.
  const checkboxClick = (e) => {
    const { name } = e.target
    const newValue = values[name] ? 0 : 1
    setFieldValue(name, newValue)
    if (name === 'status' && !newValue) {
      setFieldValue('is_additional', 0)
    }
  }

  const handleAddException = (record) => {
    setPendingExclusions(prev => [...prev, { ...record, _tempId: makeTempId(), _status: 'new' }])
  }

  const handleUpdateException = (target, record) => {
    setPendingExclusions(prev => prev.map(item => (
      item === target ? { ...item, ...record, _status: item._status === 'new' ? 'new' : 'dirty' } : item
    )))
  }

  const handleRemoveException = (target) => {
    if (target._status !== 'new' && target.peid) {
      setDeletedExclusionIds(prev => [...prev, target.peid])
    }
    setPendingExclusions(prev => prev.filter(item => item !== target))
  }

  return (
    <>
      {
        confirmation && (
          <Confirmation
            top="30px"
            left="40px"
            title="Закрыть без сохранения?"
            confirmation={confirmation}
            acceptConfirmation={() => acceptConfirmation(confirmation)}
            rejectConfirmation={rejectConfirmation}
          />
        )
      }
      <div className={styles.modalMain}>
        <div className={styles.tabWrapper}>
          <div className={styles.titleButton}>
            <Button
              onClick={() => setOpen(false)}
              className={cn({ [styles.active]: !open })}
            >
              Период
            </Button>
            <Button
              onClick={() => setOpen(true)}
              className={cn({ [styles.active]: open })}
            >
              Исключения
            </Button>
          </div>
          {!open ? (
            <>
              <div className={styles.inputsRow}>
                <Input
                  style={{ width: '750px' }}
                  label='Название'
                  name='name'
                  value={values.name}
                  error={error('name')}
                  onChange={handleChange}
                />
              </div>
              <div className={styles.inputsRow}>
                <Input
                  label='Начало'
                  name='starttime'
                  type='time'
                  value={values.starttime}
                  error={error('starttime')}
                  onChange={handleChange}
                />
                <Input
                  label='Окончание'
                  name='stoptime'
                  type='time'
                  value={values.stoptime}
                  error={error('stoptime')}
                  onChange={handleChange}
                />
              </div>
              <div className={cn(styles.inputsRow, styles.checkboxRow)}>
                <Checkbox
                  label='Учебный период'
                  name='status'
                  checked={values.status}
                  onChange={checkboxClick}
                />
                {hasIsAdditional && (
                  <Checkbox
                    label='Служебный'
                    name='is_additional'
                    checked={values.is_additional}
                    onChange={checkboxClick}
                    disabled={!values.status}
                  />
                )}
              </div>
              <div className={cn(styles.inputsRow, styles.checkboxRow)}>
                <Checkbox
                  label='Доп. образование'
                  name='dopstatus'
                  checked={values.dopstatus}
                  onChange={checkboxClick}
                />
                <Checkbox
                  label='Внеурочная деятельность'
                  name='outstatus'
                  checked={values.outstatus}
                  onChange={checkboxClick}
                />
              </div>
              <div className={styles.inputsRow}>
                <Input
                  label='Сокр. Название'
                  name='short_name'
                  value={values.short_name}
                  error={error('short_name')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className={styles.inputsRow}>
                <Select
                  label='Учебный год'
                  name='s_year_id'
                  value={values.s_year_id}
                  error={error('s_year_id')}
                  disabled
                >
                  {schoolYears?.map(renderOptions)}
                </Select>
              </div>
              <div className={styles.inputsRow}>
                <Select
                  label='Смена'
                  name='shift'
                  value={values.shift}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {shifts?.map(renderOptions)}
                </Select>
              </div>
            </>
          ) : (
            <Exceptions
              search={search}
              exclusions={pendingExclusions}
              onAdd={handleAddException}
              onUpdate={handleUpdateException}
              onRemove={handleRemoveException}
            />
          )}
        </div>
      </div>
      <ModalFooter handleSubmit={handleSubmit} closeModal={closeModal} loading={loading} />
    </>
  )
}
